import { EDPCompanyColloquial } from '../../../../models/edp/colloquials';
import { EDPRepresentativeInfo } from '../../../../models/edp/company-contact';
import { EDPCompanyInfo } from '../../../../stacking-plan.model';
import { DataObject } from '../../../../models/data-object.models';

export enum SuiteType {
  Availability = 'availability',
  Tenant = 'tenancy',
  NonRentable = 'nonrentable',
  Unknown = 'unknown',
  Lease = 'lease',
  Brand = 'brand',
  Unaccounted = 'unaccounted',
}

export interface Item {
  suiteNo: string;
  suiteId: number;
  suiteType: SuiteType;
  tenantName: string;
  areaOfSpace: number | null;
  suiteArea?: number; // QUESTION: What is the difference with areaOfSpace?
  areaOfSpaceUOM: string;
  rentRate?: number;
  rentRateUOM?: string;
  tenantAgency?: string; // QUESTION: What is the difference with tenantName?
  spaceLocation?: string;
  onMarketDate?: string; // TODO: use moment lib
  availabilityType?: string;
  vacant?: string;
  notes?: string;
  lastUpdate?: string; // TODO: use moment lib
  leaseStartDate?: string; // TODO: use moment lib
  leaseEndDate?: string; // TODO: use moment lib
  industryNaicsId?: string;
  usageType?: string;
  leaseComps?: string;
  availabilityID?: number;
  tenancyID?: number;
  companyID?: number;
  spaceClassificationTypeDesc?: string;
  nonRentableSize?: number;
  nonRentableSizeUomDesc?: string;
  brandName?: string;
  brandId?: number;
  businessCategoryDesc?: string;
}

// EDP MODELS

export interface EDPSpaceInfo {
  suite_id: number;
  suite_number: string;
  /** @new */
  suite_desc?: string;
  occupancy_status: boolean;
  availability_status: boolean;
  tenant: EDPTenantInfo;
  lease: EDPLeaseCompInfo;
  availability: EDPAvailabilityInfo;
  non_rentable_space: EDPNonRentableInfo;
  floor_number?: string;
  is_space_highlighted?: boolean;
  estimated?: boolean;
}

export interface EDPTenantInfo extends EDPCompanyInfo {
  id: number;
  space_id: number;
  active_f: boolean;
  confidentiality_f: string;
  tenancy_start_date: string; // TODO: use moment lib
  tenancy_end_date: string; // TODO: use moment lib
  edp_last_update_date: string; // TODO: use moment lib
  edp_last_update_by: string;
  tenancy_notes: string;
  tenancy_status?: string;
  brand_id: number;
  brand_name: string;
  business_category_desc: string;
  ref_property_usage_type_desc: string;
  ref_property_usage_sub_type_desc: string;
  leased_space_size: number;
  leased_space_size_uom_desc: string;
  colloquial: EDPCompanyColloquial[];
  estimated_rent_amount: number;
  estimated_rent_amount_uom: string;
  miq_record_hide_f?: boolean;
  access_control?: DataObject;
  external_opportunity_number: string;
  external_opportunity_number_source_desc: string;
  full_floor_f: boolean;
  sublease_f: boolean;
  occupancy_date: string;
  tenancy_base_rent_amount_monthly: number;
  tenancy_base_rent_amount_monthly_uom: string;
  space_condition_type_desc: string;
  layout_cbre_f?: boolean;
  estimated_rent_amount_estF?: boolean;
  tenancy_start_date_estF?: boolean;
  tenancy_end_date_estF?: boolean;
  leased_space_size_estF?: boolean;
}

export interface EDPLeaseCompInfo {
  id: number;
  /** @new */
  active_f: boolean;
  total_leased_space: number;
  ref_total_leased_space_uom_desc: string;
  sublease_f: boolean;
  asking_rent_yearly: number;
  ref_asking_rent_yearly_uom_desc: string;
  effective_rent_yearly: number;
  ref_effective_rent_yearly_uom_desc: string;
  base_rent_yearly: number;
  ref_base_rent_yearly_uom_desc: string;
  tenant_improvement_allowance: number;
  ref_tenant_improvement_allowance_uom_desc: string;
  lease_term: string;
  lease_term_std: number;
  ref_lease_term_std_uom_desc: string;
  lease_start_date: string; // TODO: use moment lib
  space_tenancy_start_date: string;
  lease_end_date: string; // TODO: use moment lib
  space_tenancy_end_date: string;
  edp_last_update_date: string; // TODO: use moment lib
  edp_last_update_by: string;
  ref_property_usage_type_desc: string;
  ref_property_usage_sub_type_desc: string;
  confidentiality_f: string;
  tenant_reps: EDPRepresentativeInfo[];
  tenant_info: EDPRepresentativeInfo[];
  brand_id: number;
  brand_name: string;
  business_category_desc: string;
  miq_record_hide_f?: boolean;
  access_control: DataObject;
  lease_arrangement_notes: string;
  tenant_status_type_desc: string;
  space_tenancy_status_desc: string;
  ref_cbre_industry_code_desc: string;
  ref_industry_classification_desc: string;
  arrangement_space_area: number;
  ref_arrangement_space_area_uom_desc: string;
  external_opportunity_number: string;
  external_opportunity_number_source_desc: string;
  full_floor_f: boolean;
  occupancy_date: string;
  space_occupancy_date: string;
  base_rent_monthly: number;
  ref_base_rent_monthly_uom_desc: string;
  space_condition_type_desc: string;
  layout_cbre_f?: boolean;
  base_rent_monthly_estimated_f?: boolean;
  lease_start_date_estimated_f?: boolean;
  lease_end_date_estimated_f?: boolean;
  arrangement_signed_date_estimated_f?: boolean;
  arrangement_end_date_estimated_f?: boolean;
  owner_occupied_f?: boolean;
  miqProperties: { userConfidentiality: string };
  total_leased_space_estimated_f?: boolean;
  asking_rent_monthly?: number;
  ref_asking_rent_monthly_uom_desc?: string;
  effective_rent_monthly?: number;
  ref_effective_rent_monthly_uom_desc?: string;
  lease_record_type_desc?: string;
  // additional field to hold space id
  space_id?: number;
}

export interface EDPAvailabilityInfo {
  id: number;
  /** @new */
  active_f: boolean;
  availability_type: string;
  property_usage_id: number;
  ref_property_usage_type_desc: string;
  ref_property_usage_sub_type_desc: string;
  date_available: string; // TODO: use moment lib
  date_on_market: string; // TODO: use moment lib
  edp_last_update_date: string; // TODO: use moment lib
  edp_last_update_by: string;
  asking_rental_rate_yearly: number;
  asking_rental_rate_yearly_uom_desc: string;
  total_area_of_space: number; // TODO: review field
  total_area_of_space_uom_desc: string; // TODO: review field
  available_space: number;
  available_space_uom_desc: string;
  full_floor_f: boolean;
  vacant_f: boolean;
  listing_notes: string;
  listing_reps: EDPRepresentativeInfo[];
  miq_record_hide_f?: boolean;
  verified_ts: string | Date;
  minimum_asking_rate_monthly: number;
  minimum_asking_rate_monthly_uom: string;
  maximum_asking_rate_monthly: number;
  maximum_asking_rate_monthly_uom: string;
  minimum_asking_rate_yearly: number;
  minimum_asking_rate_yearly_uom: string;
  maximum_asking_rate_yearly: number;
  maximum_asking_rate_yearly_uom: string;
  ref_space_availability_status_desc: string;
  ref_lease_type_desc: string; // Lease Type - ColumnId: 8 - field: ref_lease_type_desc
  ref_possession_type_desc: string; // Posession - ColumnId: 210 - field: ref_possession_type_desc
  estimated_asking_rent_f?: boolean;
  minimum_divisible_surface_area: number;
  minimum_divisible_surface_area_uom_desc: string;
  total_contiguous_area_of_space: number;
  total_contiguous_area_of_space_uom_desc: string;
}

interface EDPNonRentableInfo {
  space_classification_type_desc: string;
  non_rentable_size: number;
  non_rentable_size_uom_desc: string;
  notes: string;
}

export enum UserSettingsPreference {
  Monthly = 'monthly',
  Yearly = 'yearly',
}

export interface TenantRateCols {
  monthly: string;
  yearly: string;
}
